import { useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import {
  ContactRequest,
  LastRequest,
  RequestTypes
} from 'types/ContactRequests';
import { formatProducts, ValidPrefillData } from './helpers/helpers';
import { useTranslation } from 'react-i18next';
import { submitRequestByContext } from './helpers/requestsSubmit';
import piwik, { trackSearchEvent, trackMatomoEvent } from 'piwik/piwik';
import {
  Box,
  Button,
  Checkbox,
  DialogContentText,
  FormControl,
  FormControlLabel,
  FormHelperText,
  TextField,
  Typography
} from '@mui/material';
import BaseRequestModal from './BaseRequestModal';
import { currentPlatform } from 'components/constants/Constants';
import { RequestContactProps } from './types';
import {
  CustomContactSection,
  CustomContactSectionFormState
} from './CustomContactSection/CustomContactSection';
import { useBrandPortalEventKey } from 'hooks/useBrandPortalEventKey';
import { useAuth } from 'react-oidc-context';
import { capitalizeFirstLetter } from 'components/pages/products/ProductTable/columns/helper';

type RequestContactDialogFormState = {
  request: {
    message: string;
    acknowledge: boolean;
  };
} & CustomContactSectionFormState;

const RequestContactDialog = ({
  setModal,
  context,
  project,
  requestType,
  fullProducts,
  searchContext
}: RequestContactProps) => {
  const i18n = useTranslation();
  const methods = useForm<RequestContactDialogFormState>();
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    control,
    formState: { errors }
  } = methods;

  const [submitting, setSubmitting] = useState(false);

  const [closeVisible, setCloseVisible] = useState(false);
  const [lastRequest, setLastRequest] = useState<LastRequest>();

  const loadLastRequest = () => {
    if (lastRequest) {
      const { message } = lastRequest;
      setValue('request.message', message);
    }
  };
  const { isAuthenticated } = useAuth();

  const submitData = () => {
    setSubmitting(true);
    context.name &&
      trackMatomoEvent({
        context: context.name,
        isAuthenticated: isAuthenticated,
        where: searchContext
          ? 'Search page'
          : `${capitalizeFirstLetter(fullProducts[0].type)} product page`,
        section: fullProducts.length > 1 ? 'Bulk requests' : 'Requests',
        action: capitalizeFirstLetter(requestType),
        extraDetails: `/Submitted/${fullProducts.map(p => p.name).join(', ')}`
      });

    if (searchContext) {
      if (fullProducts.length === 1) {
        trackSearchEvent('contact-by-form', fullProducts[0].name);
      } else {
        piwik.push(['trackEvent', 'Compare', 'contact-all']);
        fullProducts.forEach((doc: any) =>
          piwik.push(['trackEvent', 'Compare', 'contact', doc.name])
        );
        piwik.push(['trackGoal', '1']);
      }
    }
    const values = getValues();

    const body: Partial<ContactRequest> = {
      type: requestType.toUpperCase() as RequestTypes,
      request: {
        ...values.request
      },
      products: formatProducts(context, fullProducts)
      // project: { ...values.project }
    };

    submitRequestByContext(
      context,
      body,
      requestType,
      setModal,
      setSubmitting,
      project
    );
  };

  const brandPortalKey = useBrandPortalEventKey();

  return (
    <form onSubmit={handleSubmit(submitData)} data-testid={requestType}>
      <BaseRequestModal
        modalSize="md"
        show={true}
        title={`Contact`}
        onSubmitRequest={submitData}
        setModal={() => setModal(requestType)}
        requestType={requestType}
        setLastRequest={setLastRequest}
        loadLastRequest={loadLastRequest}
        closeVisible={closeVisible}
        setCloseVisible={setCloseVisible}
        submitting={submitting}
      >
        <DialogContentText>
          {i18n.t(
            'contactCenter.detail.manage_request.new_request_top_message'
          )}
        </DialogContentText>
        <ValidPrefillData data={lastRequest}>
          <DialogContentText>
            {i18n.t(
              'contactCenter.detail.manage_request.new_request_top_message2'
            )}{' '}
            {requestType.toLowerCase()} request.
          </DialogContentText>
        </ValidPrefillData>
        {fullProducts?.length <= 1 && (
          <DialogContentText>
            {i18n.t(
              'contactCenter.detail.manage_request.new_request_top_message3'
            )}
            <span className="fw-bolder"> {fullProducts[0]?.name} </span>
          </DialogContentText>
        )}

        <Box sx={{ mb: 1 }}>
          <ValidPrefillData data={lastRequest}>
            <Button
              sx={{ my: 1 }}
              onClick={() => setCloseVisible(true)}
              variant="outlined"
              size="small"
              color="success"
            >
              Prefill
            </Button>
          </ValidPrefillData>
        </Box>

        {brandPortalKey !== undefined && fullProducts.length === 1 && (
          <FormProvider {...methods}>
            <CustomContactSection brandPortalKey={brandPortalKey} />
          </FormProvider>
        )}

        <Typography variant="h6" gutterBottom>
          Request Details
        </Typography>
        <Box data-testid="messages">
          <Controller
            name="request.message"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                multiline
                label={`Questions about product${
                  fullProducts.length > 1 ? 's' : ''
                }`}
                rows={3}
                placeholder="e.g. What would you like this company to do for you?"
                fullWidth
                onChange={field.onChange}
                value={field.value || ''}
                error={!!errors.request?.message}
                helperText={
                  errors.request?.message ? 'This field is required.' : ''
                }
              />
            )}
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControl error={!!errors.request?.acknowledge}>
            <FormControlLabel
              required
              control={
                <Checkbox
                  id="acknowledge"
                  {...register('request.acknowledge', { required: true })}
                />
              }
              label={`I allow ${currentPlatform} to store my information for the purpose given in this contact form.`}
              data-testid="acknowledge"
            />
            {errors.request?.acknowledge && (
              <FormHelperText>This field is required.</FormHelperText>
            )}
          </FormControl>
        </Box>
      </BaseRequestModal>
    </form>
  );
};

export default RequestContactDialog;
